import { FC, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Icon } from 'src/components';
import * as S from './styles';

const msgs = defineMessages({
  playVideo: {
    id: 'wordlist_preview_play',
    defaultMessage: 'Play video',
  },
  playAudio: {
    id: 'accessibility_dictionary_playAudio',
  },
  localSpeaker: {
    id: 'dashboard_next_lesson_local_speaker',
    defaultMessage: 'Local speaker',
  },
});

export interface MediaPreviewPanelProps {
  videoSrc: string | null;
  audioSrc: string | null;
  isAudioPlaying?: boolean;
  /** When true, autoplay the video once after mount/src change. */
  shouldAutoplayVideo?: boolean;
  onPlayAudio?: () => void;
  onVideoAutoplayHandled?: () => void;
}

export const MediaPreviewPanel: FC<MediaPreviewPanelProps> = ({
  videoSrc,
  audioSrc,
  isAudioPlaying = false,
  shouldAutoplayVideo = false,
  onPlayAudio,
  onVideoAutoplayHandled,
}) => {
  const intl = useIntl();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  useEffect(() => {
    setIsVideoPlaying(false);
  }, [videoSrc]);

  useEffect(() => {
    if (!shouldAutoplayVideo || !videoSrc) return undefined;

    const video = videoRef.current;
    if (!video) return undefined;

    void video.play().catch(() => {
      /* Autoplay can be blocked; user can still press play. */
    });
    onVideoAutoplayHandled?.();
    return undefined;
  }, [shouldAutoplayVideo, videoSrc, onVideoAutoplayHandled]);

  const toggleVideo = () => {
    const video = videoRef.current;
    /* istanbul ignore next: ref is always set when the controls are rendered */
    if (!video) return;
    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  };

  const hasVideo = !!videoSrc;
  const hasAudio = !!audioSrc;

  return (
    <S.Root data-testid="wordlist-preview-media-panel">
      <S.Stage>
        {hasVideo ? (
          <>
            <S.Video
              ref={videoRef}
              // Seek slightly past the start so the first frame renders as a poster.
              src={`${videoSrc}#t=0.1`}
              playsInline
              preload="metadata"
              onClick={toggleVideo}
              onPlay={() => setIsVideoPlaying(true)}
              onPause={() => setIsVideoPlaying(false)}
              onEnded={() => setIsVideoPlaying(false)}
              data-testid="wordlist-preview-video"
            />
            <S.Scrim aria-hidden />
            <S.Tag>
              <Icon name="videoLibraryFilled" />
              {intl.formatMessage(msgs.localSpeaker)}
            </S.Tag>
            {!isVideoPlaying && (
              <S.PlayButton
                type="button"
                onClick={toggleVideo}
                aria-label={intl.formatMessage(msgs.playVideo)}
                data-testid="wordlist-preview-play"
              >
                <Icon name="playFilled" />
              </S.PlayButton>
            )}
          </>
        ) : (
          <S.AudioOnlyStage
            data-testid="wordlist-preview-audio"
            onClick={hasAudio ? onPlayAudio : undefined}
            $isInteractive={hasAudio}
          >
            {hasAudio ? (
              <S.PlayButton
                type="button"
                onClick={event => {
                  event.stopPropagation();
                  onPlayAudio?.();
                }}
                aria-label={intl.formatMessage(msgs.playAudio)}
                data-testid="wordlist-preview-play"
              >
                <Icon name={isAudioPlaying ? 'soundFull' : 'playFilled'} />
              </S.PlayButton>
            ) : null}
          </S.AudioOnlyStage>
        )}
      </S.Stage>
    </S.Root>
  );
};
